import {
    Box,
    Flex,
    Heading,
    Skeleton,
    Text,
    Tooltip,
    LinkBox,
    LinkOverlay,
} from '@chakra-ui/react'
import Image from 'next/image'
import Link from 'next/link'
import { ResultOf } from '@graphql-typed-document-node/core'
import { gql } from 'urql'
import { Company } from 'entities/company'
import { formatJobLocationInfo, Job } from 'entities/job'
import { Location } from 'entities/location'
import { formatRemuneration } from 'entities/remuneration'
import { Tag } from 'entities/tag'
import { routes } from 'routes'

export const FRAGMENT_JOB_INFO = gql<
    Pick<Job, 'id' | 'title' | 'slug' | 'remuneration' | 'remote' | 'remoteInfo'> & {
        tags: Pick<Tag, 'id' | 'label'>[]
        company: Pick<Company, 'id' | 'name' | 'slug' | 'logo'>
        location: Pick<Location, 'city' | 'state' | 'country' | 'countryCode'>
    }
>`
    fragment JobInfo on Job {
        id
        title
        slug
        remote
        remoteInfo {
            regionRestrictions
            timezoneRestrictions {
                timezoneName
                timezoneDelta {
                    plus
                    minus
                }
            }
        }
        remuneration {
            competitive
            min
            max
            currency
            timePeriod
            equity
        }
        company {
            id
            name
            slug
            logo
        }
        location {
            city
            state
            country
            countryCode
        }
        tags {
            id
            label
        }
    }
`

export type JobInfoData = ResultOf<typeof FRAGMENT_JOB_INFO>

export const JobInfo = ({ job, isLoaded }: { job: JobInfoData; isLoaded: boolean }) => {
    const {
        title = 'default title',
        remuneration,
        company = { name: 'default name', logo: 'default logo' },
        remote,
        remoteInfo,
        location,
    } = job ?? {}

    let jobLocation = isLoaded
        ? formatJobLocationInfo({
              remote,
              remoteInfo,
              location,
          })
        : 'Default Location'

    const jobURL = isLoaded ? routes.job({ slug: job.slug }) : '#'

    return (
        <LinkBox
            mt="16px"
            borderRadius="12px"
            borderWidth="1px"
            borderColor="gray.dividers"
            pt="12px"
            pb="14px"
            px="16px"
            as={Box}
        >
            <Skeleton isLoaded={isLoaded}>
                <Heading as="h2" size="xs" fontWeight="semibold">
                    {title}
                </Heading>
            </Skeleton>

            <Skeleton isLoaded={isLoaded}>
                <Text variant="functionalButtonsSmall" color="text.secondary" mt="2px">
                    {isLoaded
                        ? remuneration && formatRemuneration(remuneration)
                        : 'Default Remuneration'}
                </Text>
            </Skeleton>

            {/* Company Information */}
            <Flex alignItems="center" mt="12px" gap="9px">
                <Skeleton isLoaded={isLoaded}>
                    {company?.logo && (
                        <Image
                            src={company?.logo}
                            alt={company?.name}
                            width="40px"
                            height="40px"
                            objectFit="cover"
                        />
                    )}
                </Skeleton>

                <Box>
                    <Skeleton isLoaded={isLoaded}>
                        <Link href={jobURL} passHref>
                            <LinkOverlay w="fit-content">
                                <Text
                                    fontWeight="medium"
                                    variant="functionalButtonsSmall"
                                    mb={'4px'}
                                >
                                    {company?.name}
                                </Text>
                            </LinkOverlay>
                        </Link>
                    </Skeleton>

                    <Skeleton isLoaded={isLoaded}>
                        <Tooltip label={jobLocation} openDelay={1000}>
                            <Text
                                noOfLines={3}
                                fontWeight="medium"
                                color="text.secondary"
                                variant="functionalButtonsSmall"
                            >
                                {jobLocation}
                            </Text>
                        </Tooltip>
                    </Skeleton>
                </Box>
            </Flex>
        </LinkBox>
    )
}
